<template>
	<div class="map-card-wrapper">
		<l-map
			ref="map"
			:key="map_render"
			:zoom="leaflet_map.zoom"
			:center="leaflet_map.center"
			:zoomControl="leaflet_map.zoomControl"
			:options="_mapoptions.options"
			:noBlockingAnimations="_mapoptions.noBlockingAnimations"
			:markerZoomAnimation="_mapoptions.markerZoomAnimation"
			@update:zoom="handleZoomUpdate"
		>
			<l-tile-layer :url="leaflet_map.url" :attribution="leaflet_map.attribution"></l-tile-layer>
			<l-feature-group ref="features" @ready="setBounds">
				<slot></slot>
				<l-marker
					v-if="enablelocation && foundlocation"
					:lat-lng="leaflet_map.my_coord_marker"
					class="user-location-marker"
				></l-marker>
			</l-feature-group>
		</l-map>
		<div v-if="enableoptions || enablelocation" class="location-btn">
			<v-btn fab small v-if="enableoptions" color="white" @click="toggleOptions()">
				<v-icon class="grey--text text--darken-4">search</v-icon>
			</v-btn>
			<v-btn fab small v-if="enablelocation" color="white" @click="geUserLocation({ inform: true })">
				<v-icon class="grey--text text--darken-4">my_location</v-icon>
			</v-btn>
		</div>
		<v-bottom-sheet
			v-if="enableoptions && optionsbottom"
			v-model="showoptions"
			lazy
			full-width
			id="options-menu"
			options-menu
		>
			<v-card tile>
				<v-toolbar v-if="!hideoptionstoolbar" light flat color="white" class="options-toolbar">
					<v-toolbar-title>{{ getStateWithParam('filter_map') }}</v-toolbar-title>
					<v-btn icon class="grey--text text--darken-4" @click="toggleOptions">
						<v-icon>close</v-icon>
					</v-btn>
				</v-toolbar>
				<v-divider v-if="!hideoptionstoolbar"></v-divider>
				<slot name="options"></slot>
			</v-card>
		</v-bottom-sheet>
	</div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LFeatureGroup } from 'vue2-leaflet';
import map_helper from '@/config/js_helper/map_helper.js';
import native_capacitor from '@/config/js_helper/native_capacitor';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
export default {
	name: 'MapController',
	components: {
		LMap,
		LTileLayer,
		LMarker,
		LFeatureGroup,
	},
	props: {
		enableoptions: {
			type: Boolean,
			default: false,
		},
		hideoptionstoolbar: {
			type: Boolean,
			default: false,
		},
		optionsbottom: {
			type: Boolean,
			default: true,
		},
		optionsright: {
			type: Boolean,
			default: false,
		},
		enablelocation: {
			type: Boolean,
			default: true,
		},
		hideoptions: {
			type: Boolean,
			default: false,
		},
		mapoptions: {
			type: Object,
		},
		fitbounds: {
			type: [Number, Boolean],
		},
	},
	data() {
		return {
			showoptions: false,
			map_render: 4000,
			icon_url: require('@/assets/img/new_icons/bus.svg'),
			foundlocation: false,
			leaflet_map: {
				zoom: 13,
				zoomControl: true,
				center: [41.3278, 19.8174],
				marker: [41.3278, 19.8174],
				my_coord_marker: [41.3278, 19.8174],
				url: 'https://{s}.tile.osm.org/{z}/{x}/{y}.png',
				attribution:
					'&copy; <a target="_blank" href="https://osm.org/copyright">OpenStreetMap</a> contributors',
			},
		};
	},
	computed: {
		_mapoptions() {
			let defaultoptions = {
				options: {
					preferCanvas: true,
				},
				markerZoomAnimation: null,
				noBlockingAnimations: true,
			};
			let mergedoptions = {};
			if (this.mapoptions) {
				mergedoptions = { ...defaultoptions, ...this.mapoptions };
				if (this.mapoptions.options) {
					defaultoptions.options = { ...defaultoptions.options, ...this.mapoptions.options };
				}
			}
			return mergedoptions;
		},
	},
	watch: {
		hideoptions(newval) {
			this.showoptions = false;
		},
		fitbounds() {
			this.updateBounds();
		},
	},
	created() {
		if (this.enablelocation) {
			this.geUserLocation();
		}
	},
	mounted() {
		map_helper.refreshMap();
	},
	methods: {
		handleZoomUpdate(newzoom) {
			this.$emit('update:zoom', newzoom);
		},
		toggleOptions() {
			this.showoptions = !this.showoptions;
		},
		async geUserLocation(options) {
			const alertshown = JSON.parse(localStorage.getItem('locationalerts'))
			const permissions = await native_capacitor.checkLocationPermissions()
			const inform = (options && options.inform) ? options.inform : false
			//console.log(alertshown, permissions, options)
			if (permissions.location === 'granted') {
				this.getCurrentPosition(inform);
			} else if( !alertshown || inform ) {
				// request permissions
				const platform = native_capacitor.Capacitor.getPlatform();
				if (platform === 'web') {
					this.getCurrentPosition(inform);
				} else {
					const confirm = await native_capacitor.showConfirm({
						title: this.getStateWithParam('location_off_title'),
						message: this.getStateWithParam('location_off_message'),
						okButtonTitle: this.getStateWithParam('settings_btn'),
						cancelButtonTitle: this.getStateWithParam('close_btn'),
					})
					if (confirm.value) {
						//console.log('open settings');
						native_capacitor.openAppSettings()
					} else {
						//console.log('Dismissed');
					}
					localStorage.setItem('locationalerts', true)
				}
			}
		},
		getCurrentPosition(inform) {
			native_capacitor
				.getCurrentPosition()
				.then((data) => {
					let newcoordinates = [data.coords.latitude, data.coords.longitude];
					this.leaflet_map.my_coord_marker = newcoordinates;
					this.$refs.map.mapObject.setView(newcoordinates, 15);
					this.foundlocation = true;
				})
				.catch((err) => {
					let message = this.getStateWithParam('location_off_message');
					if (inform) {
						native_capacitor.showToast({ text: message, duration: 'long' });
					} else {
						console.log(message);
					}
				});
		},
		getStateWithParam(param) {
			return this.$store.getters.getLanguageParameters(param);
		},
		setBounds() {
			if (this.fitbounds !== false) {
				setTimeout(() => {
					this.updateBounds();
				}, 1000);
			}
		},
		updateBounds() {
			//console.log(this.$refs.features.mapObject.getBounds())
			//console.log(this.$refs.map)
			setTimeout(() => {
				if (this.$refs.map) {
					this.$refs.map.fitBounds(this.$refs.features.mapObject.getBounds());
				}
			}, 500);
		},
	},
};
</script>

<style lang="scss" src="./MapController.scss"></style>
